import { clsx, type ClassValue } from 'clsx'
import { endOfMonth, isAfter } from 'date-fns'
import {
  fromUrl,
  parseDomain,
  type ParseResultListed,
  ParseResultType,
} from 'parse-domain'
import { createElement, type JSX } from 'react'
import { twMerge } from 'tailwind-merge'
import { CANADIAN_PROVINCES, US_STATES } from '@/config/billing'
import type { DatabaseStatus } from '@/graphql.types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function capitalize(str: string) {
  if (typeof str !== 'string') return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/** Type guard that narrows `undefined` and `null` values. */
export function isPresent<T>(t: T | undefined | null | void): t is T {
  return t !== undefined && t !== null
}

export const styled =
  <T extends keyof JSX.IntrinsicElements>(elementType: T) =>
  (...classes: Parameters<typeof cn>) => {
    const Component = ({ className, ...props }: JSX.IntrinsicElements[T]) => {
      return createElement(elementType, {
        className: cn(...classes, className),
        ...props,
      })
    }

    return Component
  }

export const mbToByte = (limit: number) => limit * 10 ** 6

export const convertMebibytesToMegabytes = (bytes: number) => {
  return (bytes / 1024 / 1024 / 1000) * 1_000_000_000
}

export const isDatabaseUpdating = (status?: DatabaseStatus | string) => {
  return [
    'updating',
    'deleting',
    'creating',
    'passwordChange',
    'restoring',
    'updatingExternalConnection',
  ].includes(status as string)
}

export const parseDomainFromURL = (url: string): ParseResultListed => {
  const wildcardRemovedUrl = url.startsWith('*.') ? url.substring(2) : url
  // Note: The parsed hostname is puny-encoded. Decode the result as necessary
  // https://github.com/peerigon/parse-domain#expected-input
  const parseResult = parseDomain(fromUrl(wildcardRemovedUrl))

  // Only allow domains listed in the public suffix list
  // https://github.com/peerigon/parse-domain#-effective-top-level-domains
  if (parseResult.type !== ParseResultType.Listed) {
    throw new Error(`"${url}" is not a valid site domain.`)
  }

  if (url.startsWith('*.')) {
    return {
      ...parseResult,
      hostname: `*.${parseResult.hostname}`,
      labels: ['*', ...parseResult.labels],
      subDomains: ['*', ...parseResult.subDomains],
    }
  }

  return parseResult
}

export const validateTimeZone = (tz: unknown): boolean => {
  try {
    if (!Intl || !Intl.DateTimeFormat().resolvedOptions().timeZone) {
      return false
    }

    if (typeof tz !== 'string') {
      return false
    }

    // throws an error if timezone is not valid
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/**
 * Generates a Laravel APP_KEY using Web APIs.
 * @returns {string} A 32-character base64 encoded key prefixed with "base64:".
 */
export function generateLaravelAppKey() {
  // Generate 32 random bytes
  const randomBytes = new Uint8Array(32)
  window.crypto.getRandomValues(randomBytes)

  // Convert the random bytes to a base64 string
  const base64Key = btoa(String.fromCharCode(...randomBytes))

  // Prefix with "base64:" as Laravel APP_KEY expects
  return `base64:${base64Key}`
}

type IsExpiredCardProps = {
  expYear?: number | null
  expMonth?: number | null
}

export const isExpiredCard = ({ expYear, expMonth }: IsExpiredCardProps) => {
  if (!expYear || !expMonth) {
    return false
  }
  return isAfter(new Date(), endOfMonth(new Date(expYear, expMonth - 1)))
}

export const isValidStateCode = (code: string): boolean =>
  Object.keys(US_STATES).includes(code)

export const isValidCanadianProvinceCode = (code: string): boolean =>
  Object.keys(CANADIAN_PROVINCES).includes(code)
